import { memo, useCallback, type ReactElement } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ToastVariant } from './toastApi.js';
import styles from './Toast.module.css';

export interface ToastRecord {
  readonly id: string;
  readonly message: string;
  readonly variant: ToastVariant;
  readonly duration: number;
}

export interface ToastViewportProps {
  readonly toasts: readonly ToastRecord[];
  readonly onDismiss: (id: string) => void;
}

function rootClassName(variant: ToastVariant): string {
  const classes = [styles.root];
  if (variant === 'success') {
    classes.push(styles.rootSuccess);
  } else if (variant === 'error') {
    classes.push(styles.rootError);
  } else if (variant === 'warning') {
    classes.push(styles.rootWarning);
  } else {
    classes.push(styles.rootInfo);
  }
  return classes.join(' ');
}

function VariantIcon({ variant }: { readonly variant: ToastVariant }): ReactElement {
  const iconProps = { className: styles.icon, 'aria-hidden': true as const, strokeWidth: 2 };
  if (variant === 'success') {
    return <CheckCircle2 {...iconProps} />;
  }
  if (variant === 'error') {
    return <AlertCircle {...iconProps} />;
  }
  if (variant === 'warning') {
    return <AlertTriangle {...iconProps} />;
  }
  return <Info {...iconProps} />;
}

export const ToastViewport = memo<ToastViewportProps>(({ toasts, onDismiss }) => {
  const { t } = useTranslation('admin');

  const createOpenChangeHandler = useCallback(
    (id: string) => (open: boolean): void => {
      if (!open) {
        onDismiss(id);
      }
    },
    [onDismiss],
  );

  return (
    <>
      {toasts.map((toast) => (
        <Toast.Root
          key={toast.id}
          className={rootClassName(toast.variant)}
          /* Infinity = persistent (error); finite = Spec dwell */
          duration={toast.duration}
          open
          onOpenChange={createOpenChangeHandler(toast.id)}
          data-testid={`toast-${toast.variant}`}
          data-toast-variant={toast.variant}
          role={toast.variant === 'error' ? 'alert' : 'status'}
        >
          <VariantIcon variant={toast.variant} />
          <Toast.Title className={styles.title} data-testid="toast-title">
            {toast.message}
          </Toast.Title>
          <Toast.Close asChild>
            <button
              type="button"
              className={styles.closeButton}
              aria-label={t('shared.notificationCloseAria')}
              data-testid="toast-close"
            >
              <X className={styles.closeIcon} aria-hidden="true" />
            </button>
          </Toast.Close>
        </Toast.Root>
      ))}
      <Toast.Viewport className={styles.viewport} data-testid="toast-viewport" />
    </>
  );
});

ToastViewport.displayName = 'ToastViewport';
